import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import ms from 'ms';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { SliderWithTitle } from '@hivebrite/design-system/Slider';

import { NewsCard } from 'hb-react/frontoffice/apps/NewsIndex/components/NewsCard';
import { NewsCardLoader } from 'hb-react/frontoffice/apps/NewsIndex/components/NewsCardLoader';
import { useTopicContext } from 'hb-react/frontoffice/contexts/TopicContext';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import { dynamicTopicResourcePath } from 'hb-react/shared/apps/Topics/utils';
import useOutboxedFontTheme from 'hb-react/shared/hooks/theme/useOutboxedFontTheme';
import { axiosWithSyntaxHandlers } from 'hb-react/shared/utils/axios';
import ThemeProvider from 'hb-react/style/ThemeProvider';

const NewsWrapper = ({ topic }) => {
  const topicContext = useTopicContext();
  const { currentUser } = useDefaultAppContexts();
  const navigate = useNavigate();
  const outboxedFontTheme = useOutboxedFontTheme();

  const newsUrl = currentUser
    ? Routes.topics_topic_news_index_path(topic.id, {
        format: 'json',
      })
    : dynamicTopicResourcePath(
        Routes.topic_news_path(topic.id, {
          format: 'json',
        }),
        topicContext,
      );

  const { data: { news = [] } = {}, isLoading } = useQuery({
    queryKey: ['topic', topic.id, 'news', 'carousel'],
    queryFn: () =>
      axiosWithSyntaxHandlers
        .get(newsUrl, {
          params: {
            per_page: 12,
          },
        })
        .then(({ data }) => data),
    enabled: topic.home_config.section_news_enabled,
    staleTime: ms('5m'),
  });

  if (!topic.home_config.section_news_enabled || news.length <= 2) {
    return null;
  }

  return (
    <ThemeProvider theme="fo" fontBrightness={outboxedFontTheme.fontBrightness}>
      <Box mb={7}>
        <Container data-testid="carousel-events">
          <SliderWithTitle
            title={topic.home_config.section_news_title || __('News')}
            link={{
              url: '../news',
              text: __('See more'),
            }}
            items={news}
            ItemComponent={({
              newCoverPicture,
              content,
              seoDescription,
              fallbackCoverPicture,
              id,
              seoTitle,
              subject,
              feedableAt,
              publishedAt,
            }) => (
              <NewsCard
                coverPicture={newCoverPicture.previewUrl}
                description={seoDescription || content}
                fallbackCoverPicture={fallbackCoverPicture}
                key={id}
                onClick={() => navigate(`../news/${id}`)}
                publishedAt={moment(feedableAt ?? publishedAt).format('LL')}
                subject={seoTitle || subject}
              />
            )}
            LoadingItemComponent={NewsCardLoader}
            loading={isLoading}
            total={news.length}
            perPageBreakpoints={{
              s: 1,
              m: 2,
              l: 3,
              xl: 3,
            }}
          />
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default NewsWrapper;

const Container = styled(Box)`
  width: 100%;
  max-width: 1143px;
  margin: auto;
  position: relative;
  flex-direction: column;
`;
